<script setup lang="ts">
import { AppTheme } from '~/types'
import { useAppStore } from '~/stores/AppStore'

interface CardGameProps {
  data: GameRecord
  invertColors?: boolean
}

const props = withDefaults(defineProps<CardGameProps>(), {
  invertColors: false
})

const store = useAppStore()
const borderColor = computed(() => {
  if (props.invertColors && store.theme === AppTheme.DARK) {
    return 'border-black'
  } else if (props.invertColors && store.theme === AppTheme.LIGHT) {
    return 'border-white'
  } else {
    return store.theme === AppTheme.DARK ? 'border-white' : 'border-black'
  }
})

const visitsFormatted = computed(() => {
  if (!props.data.visits) return
  return new Intl.NumberFormat('en', { notation: 'compact' }).format(props.data.visits)
})

const playingFormatted = computed(() => {
  if (!props.data.playing) return
  return new Intl.NumberFormat('en', { notation: 'compact' }).format(props.data.playing)
})
</script>

<template>
  <Button
    :to="data.url"
    variant="unstyled"
    class="block h-full w-full border-b-2 border-l-2 border-r-0 border-t-0 text-left"
    target="_blank"
    :class="[borderColor]"
  >
    <div class="border-b-2" :class="[borderColor]">
      <Image
        v-if="data.media"
        class="aspect-video w-full object-cover lg:aspect-square"
        loading="lazy"
        :src="data.media?.responsiveImage ? data.media?.responsiveImage?.src : data.media?.url"
        :srcset="data.media?.responsiveImage ? data.media?.responsiveImage?.webpSrcSet : undefined"
        :width="
          data.media?.responsiveImage
            ? data.media?.responsiveImage?.width
            : (data.media?.width! ?? 320)
        "
        :height="
          data.media?.responsiveImage
            ? data.media?.responsiveImage?.height
            : (data.media?.height! ?? 320)
        "
        sizes="auto"
        :alt="data.media?.alt! ?? ''"
      />
    </div>
    <div class="px-4 py-3 lg:px-6 lg:py-5">
      <div class="flex flex-col gap-1 font-bold leading-tight lg:text-lg">
        {{ data.title }}
      </div>
      <div v-if="data.visits || data.playing" class="mt-1 flex justify-between gap-4">
        <div v-if="data.visits">
          Visits:&nbsp;<span class="font-bold">{{ visitsFormatted }}</span>
        </div>
        <div v-if="data.playing" class="text-right">
          Playing:&nbsp;<span class="font-bold">{{ playingFormatted }}</span>
        </div>
      </div>
    </div>
  </Button>
</template>
